import type { PricingRule } from '../types.js';
import { WEEKDAY_LABELS } from '../types.js';

function weekdayList(days: unknown): string {
  if (!Array.isArray(days) || days.length === 0) return 'any day';
  return days
    .filter((d): d is number => typeof d === 'number' && d >= 0 && d <= 6)
    .map((d) => WEEKDAY_LABELS[d])
    .join('–');
}

/** Plain-language "when this rule applies", for the rules table. */
export function summarizeWhen(rule: PricingRule): string {
  const p = rule.condition_params ?? { type: rule.condition_type };
  switch (rule.condition_type) {
    case 'always':
      return 'Always';
    case 'time_of_day': {
      const start = (p.start_time as string) ?? '??';
      const end = (p.end_time as string) ?? '??';
      const days = p.days_of_week ? ` (${weekdayList(p.days_of_week)})` : '';
      return `${start}–${end}${days}`;
    }
    case 'date_range':
      return `${(p.start as string) ?? '?'} → ${(p.end as string) ?? '?'}`;
    case 'start_weekday':
      return `Starts ${weekdayList(p.days)}`;
    case 'end_weekday':
      return `Ends ${weekdayList(p.days)}`;
    case 'duration_days':
      return rangeLabel('day', p.min_days, p.max_days);
    case 'duration_minutes':
      return rangeLabel('min', p.min_minutes, p.max_minutes);
    case 'advance_booking':
      return `${rangeLabel('day', p.min_days, p.max_days)} ahead`;
    case 'days_from_start':
      return `${rangeLabel('day', p.min_days, p.max_days)} from start`;
    case 'voucher':
      return `With voucher (${Array.isArray(p.voucher_ids) ? p.voucher_ids.length : 0})`;
    case 'allowed_emails':
      return `Emails (${Array.isArray(p.emails) ? p.emails.length : 0})`;
    case 'user_role':
      return `Role: ${Array.isArray(p.roles) ? p.roles.join(', ') : '?'}`;
    case 'logged_in':
      return p.value ? 'Logged in' : 'Guest';
    default:
      return rule.condition_type;
  }
}

function rangeLabel(unit: string, min: unknown, max: unknown): string {
  const lo = typeof min === 'number' ? min : null;
  const hi = typeof max === 'number' ? max : null;
  if (lo !== null && hi !== null) return `${lo}–${hi} ${unit}`;
  if (lo !== null) return `≥ ${lo} ${unit}`;
  if (hi !== null) return `≤ ${hi} ${unit}`;
  return `any ${unit}`;
}

/** Plain-language "how this rule changes the price". */
export function summarizeAction(rule: PricingRule, currencySymbol = 'RM'): string {
  const verb =
    rule.update_type === 'override' ? 'set' : rule.update_type === 'increase' ? '+' : '−';
  if (rule.value_kind === 'percentage') {
    if (rule.update_type === 'override') return `set ${rule.value}%`;
    return `${verb}${rule.value}%`;
  }
  // amount: value is minor units
  const amount = `${currencySymbol}${(rule.value / 100).toFixed(2)}`;
  const unit = rule.unit ? `/${rule.unit.replace('per_', '')}` : '';
  if (rule.update_type === 'override') return `set ${amount}${unit}`;
  return `${verb}${amount}${unit}`;
}
